import { useTranslation } from "react-i18next";
import i18n from "./index";
import {
  POLICY_DISPLAY_NAMES,
  POLICY_TAG_COLOR_INDEX,
  RISK_LEVEL_MAP,
} from "#/types/xecguard";

const RISK_KEY: Record<number, string> = {
  4: "risk.high",
  3: "risk.medium",
  2: "risk.low",
  1: "risk.minimal",
};

const getPolicyLabel = (
  t: (key: string, opts?: Record<string, unknown>) => string,
  policyName: string,
): string => t(`${policyName}.name`, {
  ns: "policies",
  defaultValue: POLICY_DISPLAY_NAMES[policyName] ?? policyName,
});

export const useRiskLevelLabel = (level: number): string => {
  const { t } = useTranslation("common");
  const key = RISK_KEY[level];
  if (key) return t(key);
  return RISK_LEVEL_MAP[level]?.label ?? "";
};

export const useRiskLevelMap = (): Record<
  number,
  { label: string; color: string; bg: string }
> => {
  const { t } = useTranslation("common");
  return Object.fromEntries(
    Object.entries(RISK_LEVEL_MAP).map(([level, entry]) => {
      const k = RISK_KEY[Number(level)];
      return [
        Number(level),
        { ...entry, label: k ? t(k) : entry.label },
      ];
    }),
  );
};

export const getPolicyTagInfo = (
  policyName: string,
): { label: string; tagIndex: number } => ({
  label: i18n.t(`${policyName}.name`, {
    ns: "policies",
    defaultValue: POLICY_DISPLAY_NAMES[policyName] ?? policyName,
  }),
  tagIndex: POLICY_TAG_COLOR_INDEX[policyName] ?? 3,
});

export const usePolicyTag = (policyName: string): {
  label: string;
  tagIndex: number;
} => {
  const { t } = useTranslation("policies");
  return {
    label: getPolicyLabel(t, policyName),
    tagIndex: POLICY_TAG_COLOR_INDEX[policyName] ?? 3,
  };
};
